/**
 * The window for registering an asset and for editing one.
 *
 * One form for both, so a field means the same thing in either. The details
 * that depend on the asset's type — a light's mounting, a chiller's capacity —
 * are drawn from the JSON Schema the airports pack declares for that type,
 * not written here: a new type in the pack gets its form with no console
 * change, and the platform validates against the same schema on save.
 *
 * Editing sends only what changed, and the platform writes every change to
 * the audit log with who made it — which is what the History tab reads.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useAmAsset, useAmCreateAsset, useAmEditAsset, useAmRegister, useAmTypes } from "../../api/queries";
import { Button, Field, Input, Select } from "../../design/components";
import {
  attrProblems,
  fieldLabel,
  STAGE_LABEL,
  STAGES,
  type AmAsset,
  type AmPatch,
  type AttrProp,
  type AttrSchema,
  type LifecycleStage,
} from "../../domain/assetManagement";
import { t } from "../../i18n";
import { errorText, s } from "./shared";

interface Form {
  assetType: string;
  tag: string;
  name: string;
  zoneId: string;
  parentTag: string;
  criticality: string;
  stage: LifecycleStage;
  manufacturer: string;
  model: string;
  serialNo: string;
  installedOn: string;
  purchasedOn: string;
  purchaseCost: string;
  replacementValue: string;
  designLifeYears: string;
  warrantyUntil: string;
  attrs: Record<string, string | boolean>;
}

const EMPTY: Form = {
  assetType: "",
  tag: "",
  name: "",
  zoneId: "",
  parentTag: "",
  criticality: "3",
  stage: "in_service",
  manufacturer: "",
  model: "",
  serialNo: "",
  installedOn: "",
  purchasedOn: "",
  purchaseCost: "",
  replacementValue: "",
  designLifeYears: "",
  warrantyUntil: "",
  attrs: {},
};

export function AssetDialog({
  assetId,
  onClose,
  onSaved,
}: {
  /** absent to register a new asset */
  assetId?: string;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open && typeof d.showModal === "function") d.showModal();
  }, []);

  const detail = useAmAsset(assetId);
  const ready = !assetId || detail.data;
  return (
    <dialog ref={ref} onClose={onClose} className={s.dialog} data-testid="am-asset-dialog">
      {!ready ? (
        <p className={s.note}>{t("Loading")}…</p>
      ) : (
        <AssetForm asset={detail.data?.asset} onClose={onClose} onSaved={onSaved} />
      )}
    </dialog>
  );
}

function AssetForm({
  asset,
  onClose,
  onSaved,
}: {
  asset?: AmAsset;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const adding = !asset;
  const types = useAmTypes();
  const reg = useAmRegister({});
  const create = useAmCreateAsset();
  const edit = useAmEditAsset(asset?.id ?? "");
  const zones = useMemo(() => reg.data?.zones ?? [], [reg.data]);

  const [f, setF] = useState<Form>(() =>
    asset
      ? {
          assetType: asset.assetType,
          tag: asset.tag,
          name: asset.name,
          zoneId: "",
          parentTag: asset.parentTag ?? "",
          criticality: String(asset.criticality),
          stage: asset.stage,
          manufacturer: asset.manufacturer ?? "",
          model: asset.model ?? "",
          serialNo: asset.serialNo ?? "",
          installedOn: asset.installedOn ?? "",
          purchasedOn: asset.purchasedOn ?? "",
          purchaseCost: asset.purchaseCost?.toString() ?? "",
          replacementValue: asset.replacementValue?.toString() ?? "",
          designLifeYears: asset.designLifeYears?.toString() ?? "",
          warrantyUntil: asset.warrantyUntil ?? "",
          attrs: Object.fromEntries(
            Object.entries(asset.attrs ?? {}).map(([k, v]) => [
              k,
              typeof v === "boolean" ? v : v == null ? "" : String(v),
            ]),
          ),
        }
      : EMPTY,
  );
  const [tried, setTried] = useState(false);

  /* The zone the asset sits in, by code, once the zone list has arrived.
     The form's own zoneId stays empty until the person picks one, so the
     shown zone is worked out here rather than copied into state later. */
  const zoneIdOfAsset = zones.find((z) => z.code === asset?.zoneCode)?.id ?? "";
  const zoneShown = f.zoneId || zoneIdOfAsset;

  const type = types.data?.find((ty) => ty.key === f.assetType);
  const schema: AttrSchema = useMemo(() => type?.schema ?? {}, [type]);
  const props = Object.entries(schema.properties ?? {}).filter(([k]) => k !== "tag");
  const extra = Object.keys(f.attrs).filter((k) => !(schema.properties ?? {})[k]);
  const typedAttrs = useMemo(() => typedAttributes(f.attrs, schema), [f.attrs, schema]);
  const attrErrors = type ? attrProblems(schema, typedAttrs) : {};
  const parentByTag = new Map((reg.data?.assets ?? []).map((a) => [a.tag, a.id]));
  const parentId = f.parentTag.trim() ? parentByTag.get(f.parentTag.trim()) : null;

  const problems: Record<string, string> = {};
  if (adding && !f.assetType) problems.assetType = t("Choose the asset's type.");
  if (adding && !f.tag.trim()) problems.tag = t("A tag is needed; it is how the asset is found.");
  if (adding && f.tag.trim() && reg.data?.assets.some((a) => a.tag === f.tag.trim()))
    problems.tag = t("This tag is already used on this site.");
  if (!f.name.trim()) problems.name = t("A name is needed.");
  if (f.parentTag.trim() && !parentId) problems.parent = t("No asset on this site has that tag.");
  if (asset && parentId === asset.id) problems.parent = t("An asset cannot be its own parent.");
  for (const k of ["purchaseCost", "replacementValue", "designLifeYears"] as const)
    if (f[k].trim() && !(Number(f[k]) >= 0)) problems[k] = t("Must be a number.");
  const blocking = { ...problems, ...attrErrors };
  const show = (k: string) => (tried ? blocking[k] : undefined);

  const set = (k: keyof Form) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const num = (v: string) => (v.trim() === "" ? null : Number(v));
  const txt = (v: string) => (v.trim() === "" ? null : v.trim());
  const pending = create.isPending || edit.isPending;
  const failed = create.error ?? edit.error;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (Object.keys(blocking).length) return;
    if (adding) {
      create.mutate(
        {
          assetType: f.assetType,
          tag: f.tag.trim(),
          name: f.name.trim(),
          zoneId: zoneShown || null,
          parentId: parentId ?? null,
          criticality: Number(f.criticality),
          lifecycleStage: f.stage,
          manufacturer: txt(f.manufacturer),
          model: txt(f.model),
          serialNo: txt(f.serialNo),
          installedOn: txt(f.installedOn),
          purchasedOn: txt(f.purchasedOn),
          purchaseCost: num(f.purchaseCost),
          replacementValue: num(f.replacementValue),
          designLifeYears: num(f.designLifeYears),
          warrantyUntil: txt(f.warrantyUntil),
          attrs: Object.fromEntries(Object.entries(typedAttrs).filter(([, v]) => v !== null)),
        },
        {
          onSuccess: (r) => {
            onSaved?.(r.id);
            onClose();
          },
        },
      );
      return;
    }
    const a = asset as AmAsset;
    const patch: AmPatch = {};
    if (f.name.trim() !== a.name) patch.name = f.name.trim();
    if (f.zoneId && f.zoneId !== zoneIdOfAsset) patch.zoneId = f.zoneId;
    if ((parentId ?? null) !== a.parentId) patch.parentId = parentId ?? null;
    if (Number(f.criticality) !== a.criticality) patch.criticality = Number(f.criticality);
    if (f.stage !== a.stage) patch.lifecycleStage = f.stage;
    const pairs: [keyof AmPatch, unknown, unknown][] = [
      ["manufacturer", txt(f.manufacturer), a.manufacturer],
      ["model", txt(f.model), a.model],
      ["serialNo", txt(f.serialNo), a.serialNo],
      ["installedOn", txt(f.installedOn), a.installedOn],
      ["purchasedOn", txt(f.purchasedOn), a.purchasedOn],
      ["purchaseCost", num(f.purchaseCost), a.purchaseCost],
      ["replacementValue", num(f.replacementValue), a.replacementValue],
      ["designLifeYears", num(f.designLifeYears), a.designLifeYears],
      ["warrantyUntil", txt(f.warrantyUntil), a.warrantyUntil],
    ];
    for (const [k, now, was] of pairs) if (now !== (was ?? null)) (patch as Record<string, unknown>)[k] = now;
    const attrPatch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(typedAttrs)) {
      const before = a.attrs?.[k] ?? null;
      if (v !== before) attrPatch[k] = v;
    }
    if (Object.keys(attrPatch).length) patch.attrs = attrPatch;
    if (!Object.keys(patch).length) {
      onClose();
      return;
    }
    edit.mutate(patch, { onSuccess: onClose });
  };

  return (
    <form onSubmit={submit} className={s.dialogForm} noValidate>
      <h2>{adding ? t("Register a new asset") : t("Edit {tag}", { tag: asset?.tag ?? "" })}</h2>

      <Section title={t("What it is")}>
        {adding ? (
          <Field label="Type">
            <Select
              value={f.assetType}
              onChange={(e) => setF({ ...f, assetType: e.target.value, attrs: {} })}
              data-testid="am-new-type"
              autoFocus
            >
              <option value="">{t("Choose a type")}</option>
              {(types.data ?? []).map((ty) => (
                <option key={ty.key} value={ty.key}>
                  {ty.name}
                </option>
              ))}
            </Select>
            <Err text={show("assetType")} />
          </Field>
        ) : (
          <Field label="Type">
            <Input value={asset?.assetTypeName ?? ""} disabled />
          </Field>
        )}
        <Field label="Tag">
          <Input
            value={f.tag}
            onChange={set("tag")}
            disabled={!adding}
            placeholder={t("e.g. PMP-014")}
            data-testid="am-new-tag"
          />
          <Err text={show("tag")} />
        </Field>
        <Field label="Name">
          <Input value={f.name} onChange={set("name")} data-testid="am-new-name" />
          <Err text={show("name")} />
        </Field>
        <Field label="Criticality">
          <Select value={f.criticality} onChange={set("criticality")}>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n} — {t(["Low", "Minor", "Moderate", "High", "Critical"][n - 1])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Lifecycle stage">
          <Select value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value as LifecycleStage })}>
            {STAGES.map((st) => (
              <option key={st} value={st}>
                {t(STAGE_LABEL[st])}
              </option>
            ))}
          </Select>
        </Field>
      </Section>

      <Section title={t("Where it is")}>
        <Field label="Zone">
          <Select value={zoneShown} onChange={set("zoneId")} data-testid="am-new-zone">
            <option value="">{t("No zone")}</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name} ({z.code})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Part of (parent asset tag)">
          <Input value={f.parentTag} onChange={set("parentTag")} list="am-parent-tags" placeholder={t("optional")} />
          <datalist id="am-parent-tags">
            {(reg.data?.assets ?? []).slice(0, 2000).map((a) => (
              <option key={a.id} value={a.tag}>
                {a.name}
              </option>
            ))}
          </datalist>
          <Err text={show("parent")} />
        </Field>
      </Section>

      <Section title={t("Make and model")}>
        <Field label="Manufacturer">
          <Input value={f.manufacturer} onChange={set("manufacturer")} />
        </Field>
        <Field label="Model">
          <Input value={f.model} onChange={set("model")} />
        </Field>
        <Field label="Serial number">
          <Input value={f.serialNo} onChange={set("serialNo")} />
        </Field>
      </Section>

      <Section title={t("Dates and value")}>
        <Field label="Installed">
          <Input type="date" value={f.installedOn} onChange={set("installedOn")} data-testid="am-new-installed" />
        </Field>
        <Field label="Purchased">
          <Input type="date" value={f.purchasedOn} onChange={set("purchasedOn")} />
        </Field>
        <Field label="Purchase cost (SAR)">
          <Input inputMode="decimal" value={f.purchaseCost} onChange={set("purchaseCost")} />
          <Err text={show("purchaseCost")} />
        </Field>
        <Field label="Replacement value (SAR)">
          <Input inputMode="decimal" value={f.replacementValue} onChange={set("replacementValue")} />
          <Err text={show("replacementValue")} />
        </Field>
        <Field label="Design life (years)">
          <Input inputMode="decimal" value={f.designLifeYears} onChange={set("designLifeYears")} />
          <Err text={show("designLifeYears")} />
        </Field>
        <Field label="Warranty until">
          <Input type="date" value={f.warrantyUntil} onChange={set("warrantyUntil")} />
        </Field>
      </Section>

      {type && (props.length > 0 || extra.length > 0) && (
        <Section title={t("{type} details", { type: type.name })}>
          {props.map(([key, prop]) => (
            <AttrField
              key={key}
              name={key}
              prop={prop}
              required={(schema.required ?? []).includes(key)}
              value={f.attrs[key] ?? ""}
              onChange={(v) => setF({ ...f, attrs: { ...f.attrs, [key]: v } })}
              error={show(key)}
            />
          ))}
          {extra.map((key) => (
            <Field key={key} label={fieldLabel(key)}>
              <Input
                value={String(f.attrs[key] ?? "")}
                onChange={(e) => setF({ ...f, attrs: { ...f.attrs, [key]: e.target.value } })}
              />
            </Field>
          ))}
        </Section>
      )}

      <div className={s.formFoot}>
        <Button type="submit" variant="primary" disabled={pending} data-testid="am-new-save">
          {pending ? t("Saving…") : adding ? t("Register the asset") : t("Save changes")}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          {t("Cancel")}
        </Button>
        {tried && Object.keys(blocking).length > 0 && (
          <span className={s.err}>{t("Check the fields marked above.")}</span>
        )}
        {failed && <span className={s.err}>{errorText(failed)}</span>}
      </div>
      <p className={s.note}>{t("Every change is written to the audit log with who made it.")}</p>
    </form>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className={s.fieldset}>
      <legend>{title}</legend>
      <div className={s.form}>{children}</div>
    </fieldset>
  );
}

function Err({ text }: { text?: string }) {
  return text ? <span className={s.err}>{text}</span> : null;
}

function AttrField({
  name,
  prop,
  required,
  value,
  onChange,
  error,
}: {
  name: string;
  prop: AttrProp;
  required: boolean;
  value: string | boolean;
  onChange: (v: string | boolean) => void;
  error?: string;
}) {
  const label = `${prop.title ?? fieldLabel(name)}${required ? " *" : ""}`;
  const types = Array.isArray(prop.type) ? prop.type : prop.type ? [prop.type] : [];
  let control: ReactNode;
  if (prop.enum) {
    control = (
      <Select value={String(value)} onChange={(e) => onChange(e.target.value)} data-testid={`am-attr-${name}`}>
        <option value="">{t("Choose")}</option>
        {prop.enum.map((v) => (
          <option key={String(v)} value={String(v)}>
            {fieldLabel(String(v))}
          </option>
        ))}
      </Select>
    );
  } else if (types.includes("boolean")) {
    control = <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />;
  } else if (types.includes("number") || types.includes("integer")) {
    control = (
      <Input
        inputMode="decimal"
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`am-attr-${name}`}
      />
    );
  } else if (prop.format === "date") {
    control = <Input type="date" value={String(value)} onChange={(e) => onChange(e.target.value)} />;
  } else {
    control = (
      <Input value={String(value)} onChange={(e) => onChange(e.target.value)} data-testid={`am-attr-${name}`} />
    );
  }
  return (
    <label className={s.attrField}>
      <span className={s.dim}>{label}</span>
      {control}
      <Err text={error} />
    </label>
  );
}

/** Form strings to the types the schema asks for; an empty field is null. */
function typedAttributes(raw: Record<string, string | boolean>, schema: AttrSchema): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    const prop = schema.properties?.[k];
    const types = Array.isArray(prop?.type) ? prop.type : prop?.type ? [prop.type] : [];
    if (typeof v === "boolean") out[k] = v;
    else if (v.trim() === "") out[k] = null;
    else if (types.includes("number") || types.includes("integer")) {
      const n = Number(v);
      out[k] = Number.isFinite(n) ? n : v;
    } else if (prop?.enum && typeof prop.enum[0] === "number") out[k] = Number(v);
    else out[k] = v.trim();
  }
  return out;
}
